import type { ReactNode } from 'react';
import clsx from 'clsx';
import { CalendarIcon } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@monksflow/monks-ui';

export interface AnalysisSelectOption {
  value: string;
  label: string;
}

type AnalysisSelectVariant = 'filter' | 'section';

interface AnalysisSelectProps {
  id?: string;
  variant: AnalysisSelectVariant;
  value: string;
  onValueChange: (value: string) => void;
  ariaLabel?: string;
  placeholder?: string;
  /** Filter-variant date fields use a calendar icon instead of the default chevron. */
  icon?: 'chevron' | 'calendar';
  options?: AnalysisSelectOption[];
  children?: ReactNode;
  className?: string;
}

const filterTriggerClassName =
  'h-[38px] w-auto gap-2 rounded-lg border-border bg-card pl-3 pr-[10px] text-sm font-normal text-foreground shadow-none [&>svg]:size-3.5 [&>svg]:text-muted-foreground [&>svg]:opacity-100';

const sectionTriggerClassName =
  'h-10 w-[190px] gap-2 rounded-[10px] border-border bg-card pl-3.5 pr-3 text-sm font-medium text-foreground shadow-[0_1px_2px_rgba(0,0,0,0.05)]';

export const AnalysisSelect = ({
  id,
  variant,
  value,
  onValueChange,
  ariaLabel,
  placeholder,
  icon = 'chevron',
  options,
  children,
  className,
}: AnalysisSelectProps) => {
  // SelectTrigger always renders its own chevron with no way to swap it, so the
  // calendar icon is layered on top and the built-in chevron is hidden via CSS
  // rather than removed — see the reconciliation report for the trade-off.
  const showCalendar = variant === 'filter' && icon === 'calendar';

  const trigger = (
    <SelectTrigger
      id={id}
      aria-label={ariaLabel}
      className={clsx(
        variant === 'filter' ? filterTriggerClassName : sectionTriggerClassName,
        // Hiding the chevron frees its layout space, so reserve the prototype's 30px
        // explicitly or the value text runs underneath the calendar icon.
        showCalendar && '[&>svg]:hidden pr-[30px]',
        className,
      )}
    >
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
  );

  return (
    <Select value={value} onValueChange={onValueChange}>
      {showCalendar ? (
        <span className="relative flex">
          {trigger}
          <CalendarIcon
            data-testid="analysis-select-date-icon"
            className="pointer-events-none absolute right-[10px] top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
          />
        </span>
      ) : (
        trigger
      )}
      <SelectContent>
        {options
          ? options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))
          : children}
      </SelectContent>
    </Select>
  );
};
